import React, { useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { CircularProgress, Grid } from '@material-ui/core';
import rightArrowIcon from 'core/assets/svg/ic-right-arrow.svg';

import { Label } from 'core/components/label';
import { Button } from 'core/components/button';
import { GET_OFFER, GET_OFFER_DIFFERENCE } from 'core/store/offer-store/offer-queries';
import { buildOfferDiffChanges, dateLabel } from './helpers';
import { CompareHeaderMeta, OfferDiffTable, OfferIdPairCard } from './shared-components';
import useStyles from './compare-offer.styles';

// Compare-to-parent page (/offer/:offerId/compare): diffs an offer against its
// parentId. Free entry of any two offer IDs lives in CompareArbitraryOffers.
const CompareParentOffer = () => {
  const classes = useStyles();
  const { offerId } = useParams();

  const {
    data: currentData,
    loading: currentLoading,
    error: currentError
  } = useQuery(GET_OFFER, {
    variables: { offerId },
    fetchPolicy: 'no-cache'
  });

  const currentOffer = currentData?.offer;
  const parentOfferId = currentOffer?.parentId;

  const { data: parentData, loading: parentLoading } = useQuery(GET_OFFER, {
    variables: { offerId: parentOfferId },
    skip: !parentOfferId,
    fetchPolicy: 'no-cache'
  });

  const {
    data: diffData,
    loading: diffLoading,
    error: diffError
  } = useQuery(GET_OFFER_DIFFERENCE, {
    variables: { oldOfferId: parentOfferId, newOfferId: offerId },
    skip: !parentOfferId,
    fetchPolicy: 'network-only'
  });

  const parentOffer = parentData?.offer;

  const onViewPreviousChanges = useCallback(() => {
    if (!parentOffer?.id) {
      return;
    }
    window.open(`/offer/${parentOffer.id}/compare`, '_blank');
  }, [parentOffer]);

  const onOpenArbitraryCompare = useCallback(() => {
    window.open(`/offer/${offerId}/compare-arbitrary-offers`, '_blank');
  }, [offerId]);

  const changes = useMemo(() => buildOfferDiffChanges(diffData), [diffData]);

  if (currentLoading) {
    return (
      <div className={classes.page}>
        <CircularProgress />
      </div>
    );
  }

  if (currentError || !currentOffer) {
    return (
      <div className={classes.errorContainer}>
        <Label type="title">Offer not found</Label>
        <Label type="body1">
          The offer could not be loaded. It may be invalid or you may not have permission to view it.
        </Label>
        <Label type="body1" className={classes.errorId}>
          {offerId}
        </Label>
      </div>
    );
  }

  if (!parentOfferId) {
    return (
      <div className={classes.errorContainer}>
        <Label type="title">No previous offer</Label>
        <Label type="body1" className={classes.messageText}>
          Offer <strong>{offerId}</strong> does not have a parent offer ID — there is no previous version to compare
          against.
        </Label>
        <Grid container direction="row" alignItems="center" className={classes.noParentCta}>
          <Button variant="text" color="secondary" onClick={onOpenArbitraryCompare}>
            Trying to compare this offer to a different offer?
          </Button>
        </Grid>
      </div>
    );
  }

  const isLoading = parentLoading || diffLoading;
  const hasError = diffError || diffData?.getOfferDifference?.success === false;

  return (
    <div className={classes.page}>
      <div className={classes.header}>
        <Label type="title">Compare to parent offer</Label>
        <CompareHeaderMeta offer={currentOffer} />
        <OfferIdPairCard
          previousLabel="Parent offer ID"
          previousDate={dateLabel(parentOffer?.updatedDateTime)}
          previousOfferId={parentOfferId}
          newerLabel="Offer ID"
          newerDate={dateLabel(currentOffer.updatedDateTime)}
          newerOfferId={offerId}
          action={
            parentOffer?.parentId && (
              <Button
                variant="text"
                color="secondary"
                onClick={onViewPreviousChanges}
                className={classes.previousChangesButton}
              >
                <img src={rightArrowIcon} alt="previous" className={classes.previousChangesArrow} />
                View previous changes
              </Button>
            )
          }
          footer={
            <Button
              variant="text"
              color="secondary"
              onClick={onOpenArbitraryCompare}
              className={classes.switchModeButton}
            >
              Trying to compare this offer to a different offer?
            </Button>
          }
        />
      </div>

      {isLoading && <CircularProgress />}

      {!isLoading && hasError && (
        <div className={classes.noChanges}>
          <Label type="body1">
            {diffData?.getOfferDifference?.error ?? 'The offer difference could not be loaded.'}
          </Label>
        </div>
      )}

      {!isLoading && !hasError && changes.length === 0 && (
        <Label type="body1" className={classes.noChanges}>
          No differences found between these two offers.
        </Label>
      )}

      {!isLoading && !hasError && changes.length > 0 && (
        <OfferDiffTable
          previousHeader="Parent offer values"
          newerHeader="Offer values"
          oldOfferId={parentOfferId}
          newOfferId={offerId}
          changes={changes}
        />
      )}
    </div>
  );
};

export default CompareParentOffer;
